import {
  Citations,
  OnboardingHeader,
  OnboardingFooter,
  ExplainSlideButton,
  OnboardingNavigationMenu,
  OnboardingResultsStatusScreen,
  useSlideConfig,
  SLIDE_VIDEO_URLS,
} from '@/features/client-onboarding';
import { FullScreenLoadingOverlay } from '@/components/FullScreenLoadingOverlay';
import { SampleClientBanner } from '@/shared/components/sample-client-banner';

import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { SessionType } from '@shared';
import { selectClient, selectClientIsLoading, selectSession } from '@/model';

import type { ReactNode } from 'react';

export function OnboardingLayout({ children }: { children: ReactNode }) {
  const client = useSelector(selectClient)!;
  const session = useSelector(selectSession);
  const clientIsLoading = useSelector(selectClientIsLoading);

  const { flags } = useFeatureFlags();
  const { slidePage, citations } = useSlideConfig();

  const useFormProvider = flags.useFormProvider ?? false;

  const hasIntakeSurvey = !!client.intakeSurvey;
  const hasFormReference = !!client.formReferenceId;
  const isFormCompleted = client.formReferenceStatus === 'completed';
  const isClientSession = session.sessionType === SessionType.client;

  const shouldShowStatusScreen =
    hasFormReference &&
    !isFormCompleted &&
    !hasIntakeSurvey &&
    (isClientSession || useFormProvider);

  if (shouldShowStatusScreen) {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        onRefresh={() => window.location.reload()}
      />
    );
  }

  const videoUrl = slidePage ? SLIDE_VIDEO_URLS.get(slidePage) : undefined;

  return (
    <div className="bg-background relative flex h-dvh flex-col">
      <FullScreenLoadingOverlay isLoading={clientIsLoading} />

      {client.isSampleClient && <SampleClientBanner />}

      <div className="z-30 shrink-0">
        <OnboardingHeader />
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="relative mx-auto h-full w-full max-w-5xl">
          {children}
          {videoUrl && (
            <ExplainSlideButton
              videoUrl={videoUrl}
              className="hidden xl:block"
            />
          )}
        </div>
      </div>

      <div className="z-30 shrink-0">
        <OnboardingFooter />
      </div>

      <OnboardingNavigationMenu />
    </div>
  );
}
